import { AufnahmePunktDocument, idOf, isOrganischesSubstrat, SubstratZeile } from "src/app/data-model";
import { filzVersatz } from "../filz-tiefen";

/**
 * Vorschlag für das Entwässerungsverhalten (Gruppe I / II / III).
 *
 * Quelle: Anlage 2 (Kartierungsanleitung), Abschnitt "Entwässerungsverhalten":
 * Gruppe I = reinsandige Substrate, Gruppe II = anlehmige/lehmige Sande, Humus,
 * tonarme Schluffe, Gruppe III = Tone und Lehme; bei mehrschichtigen Profilen
 * wird über die Mächtigkeiten gewichtet, Bänder ziehen stets nach Gruppe II.
 *
 * Umgesetzt ist die Praxis-Fassung dieser Regel (Schwellen in cm, Prüffenster
 * 0–160 cm wie bei nWSK, letzte Schicht wird bis 160 cm verlängert):
 *
 *   1. Schwerer Block (L/T/U/K) ab 40 cm am Stück — aufeinanderfolgende Zeilen
 *      zählen zusammen, eine andere Schicht dazwischen trennt:
 *      setzt er oberhalb 40 cm Tiefe ein  → Gruppe III
 *      setzt er unterhalb 40 cm Tiefe ein → Gruppe II
 *   2. Summe aller anlehmigen/lehmigen/schluffigen Schichten ab 80 cm → Gruppe II
 *   3. Summe aller lehmigen/schluffigen Lagen inkl. Staubsand ab 15 cm (Bändersand)
 *      → Gruppe II
 *   4. Tiefe Unterlagerung: unterhalb 160 cm ab 40 cm am Stück aus Lehm, Ton,
 *      Schluff, Kalk oder Staubsand unter sonst reinem Sand → Gruppe II (Anlage 2)
 *   5. sonst → Gruppe I
 *
 * Alle Mächtigkeits-Schwellen sind inklusiv (genau 40/80/15 cm lösen bereits aus).
 *
 * Es gewinnt immer die schwerste zutreffende Gruppe (III vor II vor I).
 * Organische Schichten (Torf, Humus, Mudde) bleiben unberücksichtigt; Moorpunkte
 * bekommen pauschal Gruppe II.
 */

/** Prüffenster in cm (Anlage 2: Profil notfalls bis 160 cm auffüllen). */
const FENSTER = 160;
/** Ab dieser Mächtigkeit gilt eine schwere Schicht als kompakt (ab, inklusiv). */
const KOMPAKT_CM = 40;
/** Oberhalb dieser Einsatztiefe zieht eine kompakte schwere Schicht nach Gruppe III. */
const EINSATZTIEFE_CM = 40;
/** Summenschwelle für anlehmige/lehmige/schluffige Schichten (ab, inklusiv). */
const SUMME_ANLEHMIG_CM = 80;
/** Summenschwelle für lehmige Lagen und Staubsand — Bändersand (ab, inklusiv). */
const SUMME_BAENDER_CM = 15;

/**
 * Dieselben Schwellen fuer die Hilfe am (i) des Feldes
 * (EntwaesserungInfoModalComponent). Abgeschriebene Zahlen im Hilfetext
 * liefen frueher oder spaeter von der Rechnung weg.
 */
export const ENTWAESSERUNG_SCHWELLEN = {
	fensterCm: FENSTER,
	kompaktCm: KOMPAKT_CM,
	einsatztiefeCm: EINSATZTIEFE_CM,
	summeAnlehmigCm: SUMME_ANLEHMIG_CM,
	summeBaenderCm: SUMME_BAENDER_CM,
} as const;

type Kategorie = 'sand' | 'staubsand' | 'anlehmig' | 'lehmig' | 'schwer' | 'organisch';

/** Reinsandige Substrate — für sich genommen Gruppe I. */
const SAND = ['gS', 'mS', 'mfS', 'fS'];
/** Staubsand — zählt wie ein lehmiges Band in die 15-cm-Summe. */
const STAUBSAND = ['stbS'];
/** Anlehmige Sande — nur in der 80-cm-Summe. */
const ANLEHMIG = ['almS', 'alfS', 'almfS', 'algS', 'alstbS'];
/** Lehmige/schluffige Sande — 15-cm- und 80-cm-Summe (ufS wird wie lfS gewertet). */
const LEHMIG = ['lgS', 'lmS', 'lfS', 'lmfS', 'lstbS', 'ufS'];
/** Lehme, Tone, Schluffe, Kalk (K) — zusätzlich 40-cm-Regel. */
const SCHWER = ['L', 'sL', 'stL', 'UL', 'K', 'U', 'lU', 'sU', 'tU', 'T', 'uT', 'lT', 'sT'];
/** Humus, Torf, Mudden (mk wie Mudde) — bleiben unberücksichtigt. */
const ORGANISCH = [
	'ah', 'akah', 'kah', 'H', 'ih', 'akih', 'kih', 'vh', 'akvh', 'kvh', 'ph', 'akph',
	'im', 'vm', 'akim', 'akvm', 'kim', 'q', 'kq', 'akq', 'd', 'kd', 'akd', 'k', 'mk',
];

/** Kategorie eines Substratcodes; undefined = unbekannter Code → kein Vorschlag. */
function kategorie(row: SubstratZeile): Kategorie | undefined {
	if (isOrganischesSubstrat(row)) return 'organisch';
	const s = row.substrat;
	if (!s) return undefined;
	if (SAND.includes(s)) return 'sand';
	if (STAUBSAND.includes(s)) return 'staubsand';
	if (ANLEHMIG.includes(s)) return 'anlehmig';
	if (LEHMIG.includes(s)) return 'lehmig';
	if (SCHWER.includes(s)) return 'schwer';
	if (ORGANISCH.includes(s)) return 'organisch';
	return undefined;
}

/** Eine Substratzeile mit Ober-/Unterkante in cm. */
type Schicht = { ober: number, unter: number, kat: Kategorie };

/**
 * Fasst aufeinanderfolgende Schichten der gesuchten Kategorien zu Blöcken
 * zusammen ("am Stück"). Jede andere Schicht — auch eine organische — trennt.
 */
function bloecke(schichten: Schicht[], kats: Kategorie[]): { ober: number, unter: number }[] {
	const result: { ober: number, unter: number }[] = [];
	let offen: { ober: number, unter: number } | undefined;
	for (const s of schichten) {
		if (kats.includes(s.kat)) {
			if (offen) offen.unter = s.unter;
			else offen = { ober: s.ober, unter: s.unter };
		} else if (offen) {
			result.push(offen);
			offen = undefined;
		}
	}
	if (offen) result.push(offen);
	return result;
}

export const entwaesserungsverhaltenEval = (bp: any): 'I' | 'II' | 'III' | undefined => {
	const b = bp as AufnahmePunktDocument;
	const folge = b.Substratfolge;
	if (!folge?.length) return;
	// Moorpunkte: Torf trägt die Bewertung nicht — es bleibt pauschal bei Gruppe II.
	if (idOf(b.type) === 7) return 'II';

	// Leere Platzhalterzeilen am Ende abschneiden (die letzte Eingabezeile hat oft
	// noch kein Substrat) — sonst würde sie das ganze Profil als "unvollständig"
	// werten und den Vorschlag blockieren.
	let ende = folge.length;
	while (ende > 0 && kategorie(folge[ende - 1]) === undefined) ende--;
	if (ende === 0) return; // gar keine ausgefüllte Zeile

	// Schichten mit Ober-/Unterkante aufbereiten - ab Oberkante des Profils.
	// Bei einem Filz liegt die an der Oberkante des Ol (filz-tiefen.ts): Die
	// erste Mineralschicht beginnt dann unter der Auflage, und das Prueffenster
	// 0-160 cm reicht weniger tief in den Mineralboden. Die Zeilen fuehren
	// eingegebene Tiefen, also werden die Kanten verschoben, nicht die Grenzen
	// (Nutzer-Entscheidung 25.09.2026).
	const versatz = filzVersatz(b);
	const schichten: Schicht[] = [];
	for (let i = 0; i < ende; i++) {
		const kat = kategorie(folge[i]);
		if (kat === undefined) return; // echtes Loch mittendrin → kein Vorschlag
		const unter = folge[i].tiefe + versatz;
		schichten.push({
			ober: (i === 0 ? 0 : folge[i - 1].tiefe) + versatz,
			// Letzte ausgefüllte Schicht bis zum Fensterrand verlängern (wie nWSK).
			unter: i === ende - 1 ? Math.max(unter, FENSTER) : unter,
			kat,
		});
	}

	// Summen über das Prüffenster 0–160 cm; organische Schichten bleiben außen vor.
	let summeBaender = 0;
	let summeAnlehmig = 0;
	let mineralischGefunden = false;
	for (const s of schichten) {
		if (s.kat === 'organisch') continue;
		const maechtigkeit = Math.min(s.unter, FENSTER) - Math.min(s.ober, FENSTER);
		if (maechtigkeit <= 0) continue; // liegt komplett unterhalb des Fensters
		mineralischGefunden = true;
		if (s.kat === 'staubsand' || s.kat === 'lehmig' || s.kat === 'schwer') summeBaender += maechtigkeit;
		if (s.kat === 'anlehmig' || s.kat === 'lehmig' || s.kat === 'schwer') summeAnlehmig += maechtigkeit;
	}
	if (!mineralischGefunden) return;

	// Regel 1: geschlossener Block aus L/T/U/K ab 40 cm im Prüffenster.
	let gruppeIII = false;
	let gruppeII = false;
	for (const block of bloecke(schichten, ['schwer'])) {
		const imFenster = Math.min(block.unter, FENSTER) - Math.min(block.ober, FENSTER);
		if (imFenster < KOMPAKT_CM) continue;
		if (block.ober < EINSATZTIEFE_CM) gruppeIII = true;
		else gruppeII = true;
	}

	// Regel 4 (Anlage 2): tiefe Unterlagerung — unterhalb 160 cm ab 40 cm am Stück
	// aus Lehm, Ton, Schluff, Kalk oder Staubsand.
	const tiefeUnterlagerung = bloecke(schichten, ['schwer', 'staubsand'])
		.some(block => block.unter - Math.max(block.ober, FENSTER) >= KOMPAKT_CM);

	// Schwerste zutreffende Gruppe gewinnt.
	if (gruppeIII) return 'III';
	if (gruppeII || summeAnlehmig >= SUMME_ANLEHMIG_CM || summeBaender >= SUMME_BAENDER_CM) return 'II';
	// Sonst reiner Sand — es sei denn, darunter folgt noch schweres Material.
	if (tiefeUnterlagerung) return 'II';
	return 'I';
};

/**
 * Trägt den Vorschlag ins Protokoll ein und zieht ihn bei Änderungen an der
 * Substratfolge nach — aber nur, solange der Wert vom Vorschlag stammt.
 *
 * Sobald der Kartierer das Feld selbst ändert, weicht es vom gemerkten Vorschlag
 * ab und wird nicht mehr angefasst. Werte aus Altdaten und Importen haben keinen
 * gemerkten Vorschlag und gelten damit ebenfalls als von Hand gesetzt.
 */
export function applyEntwaesserungsVorschlag(bp: AufnahmePunktDocument | undefined): void {
	const eh = bp?.extendedHeader;
	if (!eh) return;
	const wert = eh.entwaesserungsverhalten;
	const gemerkt = eh.entwaesserungVorschlag;
	// Von Hand gesetzt (oder Altbestand/Import) → eingefroren.
	if (wert && wert !== gemerkt) return;

	const vorschlag = entwaesserungsverhaltenEval(bp);
	if (!vorschlag) return;
	eh.entwaesserungsverhalten = vorschlag;
	eh.entwaesserungVorschlag = vorschlag;
}
